import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, DollarSign, Search, Filter, Plus, Loader2,
  CheckCircle, XCircle, Clock, CreditCard, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Wallet, Settings, Save, Truck, CalendarIcon, Mail, Send
} from "lucide-react";

const STATUS_BADGES = {
  approval_pending: { label: "Approval Pending", className: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Approved", className: "bg-blue-100 text-blue-700 border-blue-200" },
  rejected: { label: "Rejected", className: "bg-red-100 text-red-700 border-red-200" },
  paid: { label: "Paid", className: "bg-green-100 text-green-700 border-green-200" },
};

const PnLReport = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useLocalStorageState("pnl-report:tab", "pnl");

  // ====== P&L State ======
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ total_revenue: 0, total_pnl: 0, total_pnl_percent: 0 });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  // Filters persisted across reloads/navigation
  const [searchLRN, setSearchLRN] = useLocalStorageState("pnl-report:searchLRN", "");
  const [selectedBrand, setSelectedBrand] = useLocalStorageState("pnl-report:brand", "");
  const [dateFrom, setDateFrom] = useLocalStorageState("pnl-report:dateFrom", "");
  const [dateTo, setDateTo] = useLocalStorageState("pnl-report:dateTo", "");
  const [pnlMin, setPnlMin] = useLocalStorageState("pnl-report:pnlMin", "");
  const [pnlMax, setPnlMax] = useLocalStorageState("pnl-report:pnlMax", "");
  const [brands, setBrands] = useState([]);

  // ====== SP Payment State ======
  const [spPayments, setSpPayments] = useState([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spPage, setSpPage] = useState(1);
  const [spTotalPages, setSpTotalPages] = useState(1);
  const [spSummary, setSpSummary] = useState({ total_paid: 0, count: 0 });
  const [partners, setPartners] = useState([]);

  // Modal state
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [modalPartner, setModalPartner] = useState("");
  const [modalPartnerName, setModalPartnerName] = useState("");
  const [modalType, setModalType] = useState("");
  const [modalAmount, setModalAmount] = useState("");
  const [modalNotes, setModalNotes] = useState("");
  const [modalPaymentDate, setModalPaymentDate] = useState(new Date());
  const [modalTxnNumber, setModalTxnNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Mark-paid modal state
  const [markPaidModalOpen, setMarkPaidModalOpen] = useState(false);
  const [markPaidRecordId, setMarkPaidRecordId] = useState(null);
  const [markPaidTxn, setMarkPaidTxn] = useState("");

  // Settings state
  const [spSettingsTab, setSpSettingsTab] = useState("list");
  const [approverEmails, setApproverEmails] = useState([]);
  const [newApproverEmail, setNewApproverEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [notifToEmails, setNotifToEmails] = useState([]);
  const [notifCcEmails, setNotifCcEmails] = useState([]);
  const [notifBccEmails, setNotifBccEmails] = useState([]);
  const [newNotifTo, setNewNotifTo] = useState("");
  const [newNotifCc, setNewNotifCc] = useState("");
  const [newNotifBcc, setNewNotifBcc] = useState("");

  // ====== P&L functions ======
  useEffect(() => { fetchBrands(); fetchSpSummary(); }, []);
  useEffect(() => { if (mainTab === "pnl") fetchData(); }, [currentPage, mainTab]);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/pnl/brands`);
      setBrands(res.data.brands);
    } catch { /* noop */ }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", 50);
      if (searchLRN) params.append("search", searchLRN);
      if (selectedBrand && selectedBrand !== "all") params.append("brand_name", selectedBrand);
      if (dateFrom) params.append("date_from", new Date(dateFrom).toISOString());
      if (dateTo) params.append("date_to", new Date(dateTo).toISOString());
      if (pnlMin) params.append("pnl_min", pnlMin);
      if (pnlMax) params.append("pnl_max", pnlMax);
      const res = await axios.get(`${API}/pnl/report?${params.toString()}`);
      setData(res.data.data);
      setSummary(res.data.summary);
      setPagination(res.data.pagination);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  const handleApplyFilters = () => { setCurrentPage(1); fetchData(); };
  const handleClearFilters = () => {
    setSearchLRN(""); setSelectedBrand(""); setDateFrom(""); setDateTo(""); setPnlMin(""); setPnlMax("");
    setCurrentPage(1);
    setTimeout(() => fetchData(), 100);
  };

  // ====== SP Payment functions ======
  useEffect(() => {
    if (mainTab === "sp_payments") {
      fetchSpPayments();
      fetchSpSummary();
      fetchPartners();
      fetchSpSettings();
    }
  }, [mainTab, spPage]);

  const fetchSpPayments = async () => {
    setSpLoading(true);
    try {
      const res = await axios.get(`${API}/sp-payments/list?page=${spPage}&size=20`);
      setSpPayments(res.data.data || []);
      setSpTotalPages(res.data.total_pages || 1);
    } catch { toast.error("Failed to load payments"); }
    finally { setSpLoading(false); }
  };

  const fetchSpSummary = async () => {
    try {
      const res = await axios.get(`${API}/sp-payments/summary`);
      setSpSummary(res.data);
    } catch { /* noop */ }
  };

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API}/b2b-shipping-partner/`);
      const list = Array.isArray(res.data) ? res.data : (res.data.partners || res.data.data || []);
      setPartners(list);
    } catch { /* noop */ }
  };

  const fetchSpSettings = async () => {
    try {
      const res = await axios.get(`${API}/sp-payments/settings`);
      setApproverEmails(res.data.approver_emails || []);
      setNotifToEmails(res.data.notification_to_emails || []);
      setNotifCcEmails(res.data.notification_cc_emails || []);
      setNotifBccEmails(res.data.notification_bcc_emails || []);
    } catch { /* noop */ }
  };

  const handleCreatePayment = async () => {
    if (!modalPartner || !modalType || !modalAmount) return;
    if (modalType === "payment_addition" && !modalTxnNumber.trim()) {
      toast.error("Transaction number is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        shipping_partner_id: modalPartner,
        shipping_partner_name: modalPartnerName,
        request_type: modalType,
        amount: parseFloat(modalAmount),
        notes: modalNotes,
      };
      if (modalType === "payment_addition") {
        payload.payment_date = format(modalPaymentDate, "dd/MM/yyyy");
        payload.transaction_number = modalTxnNumber.trim();
      }
      await axios.post(`${API}/sp-payments/create`, payload);
      toast.success(modalType === "payment_addition" ? "Payment added" : "Recharge request created & approval email sent");
      setPaymentModalOpen(false);
      resetModal();
      fetchSpPayments();
      fetchSpSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally { setSubmitting(false); }
  };

  const resetModal = () => {
    setModalPartner(""); setModalPartnerName(""); setModalType(""); setModalAmount(""); setModalNotes("");
    setModalPaymentDate(new Date()); setModalTxnNumber("");
  };

  const handleAction = async (id, action) => {
    setActionLoading(`${id}-${action}`);
    try {
      await axios.post(`${API}/sp-payments/${id}/${action}`);
      toast.success(`${action === "approve" ? "Approved" : "Rejected"}`);
      fetchSpPayments();
      fetchSpSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally { setActionLoading(null); }
  };

  const handleResendApproval = async (id) => {
    setActionLoading(`${id}-resend`);
    try {
      await axios.post(`${API}/sp-payments/${id}/resend-approval`);
      toast.success("Approval email re-sent");
      fetchSpPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally { setActionLoading(null); }
  };

  const openMarkPaidModal = (id) => {
    setMarkPaidRecordId(id);
    setMarkPaidTxn("");
    setMarkPaidModalOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!markPaidTxn.trim()) { toast.error("Transaction number is required"); return; }
    setActionLoading(`${markPaidRecordId}-mark-paid`);
    setMarkPaidModalOpen(false);
    try {
      await axios.post(`${API}/sp-payments/${markPaidRecordId}/mark-paid`, { transaction_number: markPaidTxn.trim() });
      toast.success("Marked as Paid");
      fetchSpPayments();
      fetchSpSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to mark paid");
    } finally { setActionLoading(null); setMarkPaidRecordId(null); }
  };

  const saveApproverSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/sp-payments/settings`, {
        approver_emails: approverEmails,
        notification_to_emails: notifToEmails,
        notification_cc_emails: notifCcEmails,
        notification_bcc_emails: notifBccEmails,
      });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSavingSettings(false); }
  };

  const addApproverEmail = () => {
    const email = newApproverEmail.trim().toLowerCase();
    if (email && !approverEmails.includes(email)) {
      setApproverEmails([...approverEmails, email]);
      setNewApproverEmail("");
    }
  };

  const removeApproverEmail = (email) => {
    setApproverEmails(approverEmails.filter(e => e !== email));
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-IN", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };
  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  };
  const formatTimeOnly = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Profit & Loss Report</h1>
          <p className="text-slate-600">Track revenue, profitability & shipping partner payments</p>
        </div>

        {/* Summary Cards - always visible */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div>
            <p className="text-sm text-slate-600">Total Revenue</p>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_revenue)}</p>
            <p className="text-xs text-slate-500 mt-1">Charged to Sellers</p>
          </div><div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-blue-600" /></div></div></CardContent></Card>

          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div>
            <p className="text-sm text-slate-600">Total Charged by SP</p>
            <p className="text-2xl font-bold text-orange-700">{formatCurrency(summary.total_revenue - summary.total_pnl)}</p>
            <p className="text-xs text-slate-500 mt-1">Shipping Partner Cost</p>
          </div><div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center"><Truck className="w-5 h-5 text-orange-600" /></div></div></CardContent></Card>

          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div>
            <p className="text-sm text-slate-600">Total P&L</p>
            <p className={`text-2xl font-bold ${summary.total_pnl >= 0 ? "text-green-600" : "text-red-600"}`}>{formatCurrency(summary.total_pnl)}</p>
            <p className="text-xs text-slate-500 mt-1">Revenue - SP Cost</p>
          </div><div className={`w-10 h-10 rounded-full flex items-center justify-center ${summary.total_pnl >= 0 ? "bg-green-100" : "bg-red-100"}`}>
            {summary.total_pnl >= 0 ? <TrendingUp className="w-5 h-5 text-green-600" /> : <TrendingDown className="w-5 h-5 text-red-600" />}
          </div></div></CardContent></Card>

          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div>
            <p className="text-sm text-slate-600">P&L %</p>
            <p className={`text-2xl font-bold ${summary.total_pnl_percent >= 0 ? "text-green-600" : "text-red-600"}`}>{(summary.total_pnl_percent || 0).toFixed(2)}%</p>
            <p className="text-xs text-slate-500 mt-1">Profit Margin</p>
          </div><div className={`w-10 h-10 rounded-full flex items-center justify-center ${summary.total_pnl_percent >= 0 ? "bg-green-100" : "bg-red-100"}`}>
            <span className={`text-lg font-bold ${summary.total_pnl_percent >= 0 ? "text-green-600" : "text-red-600"}`}>%</span>
          </div></div></CardContent></Card>

          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div>
            <p className="text-sm text-slate-600">Payment to SP</p>
            <p className="text-2xl font-bold text-indigo-700">{formatCurrency(spSummary.total_paid)}</p>
            <p className="text-xs text-slate-500 mt-1">{spSummary.count} transaction{spSummary.count !== 1 ? "s" : ""}</p>
          </div><div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center"><Wallet className="w-5 h-5 text-indigo-600" /></div></div></CardContent></Card>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="pnl" className="gap-2" data-testid="pnl-tab"><DollarSign className="w-4 h-4" /> P&L Report</TabsTrigger>
            <TabsTrigger value="sp_payments" className="gap-2" data-testid="sp-payments-tab">
              <CreditCard className="w-4 h-4" /> Payment/Recharge History - Shipping Partner
              {spSummary.pending_count > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded-full">{spSummary.pending_count}</span>}
            </TabsTrigger>
          </TabsList>

          {/* ======= P&L TAB ======= */}
          <TabsContent value="pnl">
            {/* Filters */}
            <Card className="mb-6">
              <CardHeader><CardTitle className="flex items-center gap-2"><Filter className="w-5 h-5" />Filters</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div><Label>Search LRN</Label><div className="relative"><Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" /><Input placeholder="Enter LRN" value={searchLRN} onChange={(e) => setSearchLRN(e.target.value)} className="pl-8" /></div></div>
                  <div><Label>Brand Name</Label><Select value={selectedBrand} onValueChange={setSelectedBrand}><SelectTrigger><SelectValue placeholder="All Brands" /></SelectTrigger><SelectContent><SelectItem value="all">All Brands</SelectItem>{brands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Date From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
                  <div><Label>Date To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
                  <div><Label>P&L % Min</Label><Input type="number" placeholder="e.g., -10" value={pnlMin} onChange={(e) => setPnlMin(e.target.value)} step="0.1" /></div>
                  <div><Label>P&L % Max</Label><Input type="number" placeholder="e.g., 50" value={pnlMax} onChange={(e) => setPnlMax(e.target.value)} step="0.1" /></div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button onClick={handleApplyFilters} style={{backgroundColor: "#00A699"}}>Apply Filters</Button>
                  <Button variant="outline" onClick={handleClearFilters}>Clear Filters</Button>
                </div>
              </CardContent>
            </Card>

            {/* P&L Table */}
            <Card>
              <CardContent className="pt-6">
                {loading && data.length === 0 ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : data.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b">
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">LRN</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Brand Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Timestamp</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Charged to Seller</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Charged by SP</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">P&L</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">P&L %</th>
                        </tr></thead>
                        <tbody>
                          {data.map((row, i) => (
                            <tr key={i} className="border-b hover:bg-slate-50">
                              <td className="px-4 py-3 align-middle text-sm">{row.lrn || <span className="text-slate-400">-</span>}</td>
                              <td className="px-4 py-3 align-middle text-sm">{row.brand_name || <span className="text-slate-400">-</span>}</td>
                              <td className="px-4 py-3 align-middle text-sm text-slate-600">
                                <div className="whitespace-nowrap">
                                  <div>{formatDateOnly(row.timestamp)}</div>
                                  <div className="text-xs text-slate-500">{formatTimeOnly(row.timestamp)}</div>
                                </div>
                              </td>
                              <td className="px-4 py-3 align-middle text-sm text-right font-medium">{formatCurrency(row.charged_to_seller)}</td>
                              <td className="px-4 py-3 align-middle text-sm text-right">{formatCurrency(row.charged_by_sp)}</td>
                              <td className={`px-4 py-3 align-middle text-sm text-right font-medium ${row.pnl_amount >= 0 ? "text-green-600" : "text-red-600"}`}>{formatCurrency(row.pnl_amount)}</td>
                              <td className={`px-4 py-3 align-middle text-sm text-right font-medium ${row.pnl_percent >= 0 ? "text-green-600" : "text-red-600"}`}>{row.pnl_percent.toFixed(2)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {pagination.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t mt-4">
                        <div className="text-sm text-slate-600">Page {currentPage} of {pagination.pages} ({pagination.total_all || pagination.total} records)</div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))} disabled={currentPage === pagination.pages}>Next</Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12"><p className="text-slate-500">No data found</p></div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ======= SP PAYMENTS TAB ======= */}
          <TabsContent value="sp_payments">
            <Tabs value={spSettingsTab} onValueChange={setSpSettingsTab}>
              <div className="flex items-center justify-between mb-6">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="list" className="gap-2"><CreditCard className="w-4 h-4" /> History</TabsTrigger>
                  <TabsTrigger value="settings" className="gap-2"><Settings className="w-4 h-4" /> Approver Settings</TabsTrigger>
                </TabsList>
                <Button onClick={() => { resetModal(); setPaymentModalOpen(true); }} style={{backgroundColor: "#1B8C3D"}} data-testid="add-payment-btn">
                  <Plus className="w-4 h-4 mr-2" /> Payment / Recharge
                </Button>
              </div>

              {/* History Sub-Tab */}
              <TabsContent value="list">
                <Card>
                  <CardContent className="pt-6">
                    {spLoading ? (
                      <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                    ) : spPayments.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">No payment records yet</div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="sp-payments-table">
                            <thead><tr className="border-b bg-muted/50">
                              <th className="w-8 px-4 py-3"></th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Shipping Partner</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Txn #</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Payment Date</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Notes</th>
                              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Created By</th>
                              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Actions</th>
                            </tr></thead>
                            <tbody>
                              {spPayments.map((row) => {
                                const badge = STATUS_BADGES[row.status] || { label: row.status, className: "bg-gray-100 text-gray-700" };
                                const isExpanded = expandedRow === row.id;
                                return (
                                  <React.Fragment key={row.id}>
                                  <tr className={`border-b hover:bg-muted/30 cursor-pointer ${isExpanded ? "bg-muted/20" : ""}`} onClick={() => setExpandedRow(isExpanded ? null : row.id)}>
                                    <td className="px-4 py-3 align-middle text-center text-slate-400">
                                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </td>
                                    <td className="px-4 py-3 align-middle whitespace-nowrap">
                                      <div>{formatDateOnly(row.created_at)}</div>
                                      <div className="text-xs text-slate-500">{formatTimeOnly(row.created_at)}</div>
                                    </td>
                                    <td className="px-4 py-3 align-middle font-medium">{row.shipping_partner_name}</td>
                                    <td className="px-4 py-3 align-middle">
                                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${row.request_type === "recharge_request" ? "bg-purple-100 text-purple-700" : "bg-teal-100 text-teal-700"}`}>
                                        {row.request_type === "recharge_request" ? "Recharge" : "Payment"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 align-middle text-right font-medium">{formatCurrency(row.amount)}</td>
                                    <td className="px-4 py-3 align-middle text-slate-600">{row.transaction_number || <span className="text-slate-300">-</span>}</td>
                                    <td className="px-4 py-3 align-middle text-slate-600 whitespace-nowrap">{row.payment_date || <span className="text-slate-300">-</span>}</td>
                                    <td className="px-4 py-3 align-middle text-slate-500 max-w-[160px] truncate">{row.notes || "-"}</td>
                                    <td className="px-4 py-3 align-middle text-slate-600">{row.created_by}</td>
                                    <td className="px-4 py-3 align-middle text-center">
                                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${badge.className}`}>{badge.label}</span>
                                    </td>
                                    <td className="px-4 py-3 align-middle text-center" onClick={(e) => e.stopPropagation()}>
                                      <div className="flex items-center justify-center gap-1">
                                        {row.status === "approval_pending" && (
                                          <>
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-green-600 hover:bg-green-50"
                                              onClick={() => handleAction(row.id, "approve")}
                                              disabled={!!actionLoading}
                                              data-testid={`approve-${row.id}`}>
                                              {actionLoading === `${row.id}-approve` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50"
                                              onClick={() => handleAction(row.id, "reject")}
                                              disabled={!!actionLoading}
                                              data-testid={`reject-${row.id}`}>
                                              {actionLoading === `${row.id}-reject` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-600 hover:bg-amber-50"
                                              onClick={() => handleResendApproval(row.id)}
                                              disabled={!!actionLoading}
                                              title="Resend approval email"
                                              data-testid={`resend-${row.id}`}>
                                              {actionLoading === `${row.id}-resend` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                            </Button>
                                          </>
                                        )}
                                        {row.status === "approved" && (
                                          <Button size="sm" variant="outline" className="h-7 px-2 text-green-700 border-green-200 hover:bg-green-50"
                                            onClick={() => openMarkPaidModal(row.id)}
                                            disabled={!!actionLoading}
                                            data-testid={`mark-paid-${row.id}`}>
                                            {actionLoading === `${row.id}-mark-paid` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CreditCard className="w-3 h-3 mr-1" />Paid</>}
                                          </Button>
                                        )}
                                        {row.status === "paid" && <span className="text-green-600"><CheckCircle className="w-4 h-4" /></span>}
                                        {row.status === "rejected" && <span className="text-red-400"><XCircle className="w-4 h-4" /></span>}
                                      </div>
                                    </td>
                                  </tr>
                                  {/* Expandable Audit Log Row */}
                                  {isExpanded && row.audit_log && row.audit_log.length > 0 && (
                                    <tr className="bg-slate-50/80">
                                      <td colSpan={11} className="px-4 py-3">
                                        <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Activity Log</div>
                                        <div className="relative pl-4 border-l-2 border-slate-200 space-y-2">
                                          {row.audit_log.map((log, idx) => (
                                            <div key={idx} className="relative">
                                              <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white"
                                                style={{ backgroundColor: log.action === "created" || log.action === "created_as_paid" ? "#00A699" : log.action === "approved" ? "#16a34a" : log.action === "rejected" ? "#dc2626" : log.action === "marked_paid" ? "#059669" : "#94a3b8" }} />
                                              <div className="flex items-baseline gap-2">
                                                <span className="font-medium text-slate-700 text-xs capitalize">{log.action.replace(/_/g, " ")}</span>
                                                <span className="text-slate-400 text-xs">by {log.by}</span>
                                                <span className="text-slate-400 text-xs">{formatDate(log.at)}</span>
                                              </div>
                                              {log.note && <p className="text-slate-500 text-xs mt-0.5">{log.note}</p>}
                                            </div>
                                          ))}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {spTotalPages > 1 && (
                          <div className="flex items-center justify-between mt-4 pt-4 border-t">
                            <span className="text-sm text-muted-foreground">Page {spPage} of {spTotalPages}</span>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => setSpPage(p => Math.max(1, p - 1))} disabled={spPage === 1}><ChevronLeft className="w-4 h-4" /></Button>
                              <Button variant="outline" size="sm" onClick={() => setSpPage(p => Math.min(spTotalPages, p + 1))} disabled={spPage === spTotalPages}><ChevronRight className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Approver Settings Sub-Tab */}
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-slate-600" /> Approval Authority</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-500">Add email addresses of people who can approve recharge requests. They will receive an email with Approve/Reject links.</p>
                    <div className="flex gap-2">
                      <Input value={newApproverEmail} onChange={(e) => setNewApproverEmail(e.target.value)} placeholder="Enter email address"
                        onKeyDown={(e) => { if (e.key === "Enter") addApproverEmail(); }} data-testid="approver-email-input" className="max-w-sm" />
                      <Button variant="outline" onClick={addApproverEmail} data-testid="add-approver-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
                    </div>
                    {approverEmails.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {approverEmails.map((email) => (
                          <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-sm">
                            {email}
                            <button onClick={() => removeApproverEmail(email)} className="text-slate-400 hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-600">No approvers configured. Recharge requests won't send approval emails.</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-slate-600" /> Decision Notification (Accept/Reject)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-slate-500">When a recharge request is accepted or rejected (via email or dashboard), a notification email will be sent to these recipients.</p>

                    {/* To */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">To</label>
                      <div className="flex gap-2 mt-1">
                        <Input value={newNotifTo} onChange={e => setNewNotifTo(e.target.value)} placeholder="Add To email"
                          onKeyDown={e => { if (e.key === "Enter") { const em = newNotifTo.trim().toLowerCase(); if (em && !notifToEmails.includes(em)) { setNotifToEmails([...notifToEmails, em]); setNewNotifTo(""); }}}}
                          className="max-w-sm" data-testid="notif-to-input" />
                        <Button variant="outline" onClick={() => { const em = newNotifTo.trim().toLowerCase(); if (em && !notifToEmails.includes(em)) { setNotifToEmails([...notifToEmails, em]); setNewNotifTo(""); }}} data-testid="add-notif-to"><Plus className="w-4 h-4" /></Button>
                      </div>
                      {notifToEmails.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {notifToEmails.map(em => (
                            <span key={em} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm">
                              {em}
                              <button onClick={() => setNotifToEmails(notifToEmails.filter(e => e !== em))} className="text-teal-400 hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* CC */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">CC</label>
                      <div className="flex gap-2 mt-1">
                        <Input value={newNotifCc} onChange={e => setNewNotifCc(e.target.value)} placeholder="Add CC email"
                          onKeyDown={e => { if (e.key === "Enter") { const em = newNotifCc.trim().toLowerCase(); if (em && !notifCcEmails.includes(em)) { setNotifCcEmails([...notifCcEmails, em]); setNewNotifCc(""); }}}}
                          className="max-w-sm" data-testid="notif-cc-input" />
                        <Button variant="outline" onClick={() => { const em = newNotifCc.trim().toLowerCase(); if (em && !notifCcEmails.includes(em)) { setNotifCcEmails([...notifCcEmails, em]); setNewNotifCc(""); }}} data-testid="add-notif-cc"><Plus className="w-4 h-4" /></Button>
                      </div>
                      {notifCcEmails.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {notifCcEmails.map(em => (
                            <span key={em} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                              {em}
                              <button onClick={() => setNotifCcEmails(notifCcEmails.filter(e => e !== em))} className="text-blue-400 hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* BCC */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">BCC</label>
                      <div className="flex gap-2 mt-1">
                        <Input value={newNotifBcc} onChange={e => setNewNotifBcc(e.target.value)} placeholder="Add BCC email"
                          onKeyDown={e => { if (e.key === "Enter") { const em = newNotifBcc.trim().toLowerCase(); if (em && !notifBccEmails.includes(em)) { setNotifBccEmails([...notifBccEmails, em]); setNewNotifBcc(""); }}}}
                          className="max-w-sm" data-testid="notif-bcc-input" />
                        <Button variant="outline" onClick={() => { const em = newNotifBcc.trim().toLowerCase(); if (em && !notifBccEmails.includes(em)) { setNotifBccEmails([...notifBccEmails, em]); setNewNotifBcc(""); }}} data-testid="add-notif-bcc"><Plus className="w-4 h-4" /></Button>
                      </div>
                      {notifBccEmails.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {notifBccEmails.map(em => (
                            <span key={em} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
                              {em}
                              <button onClick={() => setNotifBccEmails(notifBccEmails.filter(e => e !== em))} className="text-gray-400 hover:text-red-500"><XCircle className="w-3.5 h-3.5" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="mt-6">
                  <Button onClick={saveApproverSettings} disabled={savingSettings} style={{ backgroundColor: "#1B8C3D" }} data-testid="save-approver-settings">
                    {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {savingSettings ? "Saving..." : "Save All Settings"}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>

        {/* Payment/Recharge Modal */}
        <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="payment-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-indigo-600" /> Payment / Recharge</DialogTitle>
              <DialogDescription>Add a new payment or recharge request for a shipping partner</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Shipping Partner <span className="text-red-500">*</span></Label>
                <Select value={modalPartner} onValueChange={(val) => {
                  setModalPartner(val);
                  const p = partners.find(p => p.id === val);
                  setModalPartnerName(p?.name || "");
                }}>
                  <SelectTrigger className="mt-1" data-testid="partner-select"><SelectValue placeholder="Select shipping partner" /></SelectTrigger>
                  <SelectContent>
                    {partners.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type <span className="text-red-500">*</span></Label>
                <Select value={modalType} onValueChange={setModalType}>
                  <SelectTrigger className="mt-1" data-testid="type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recharge_request">Recharge Request</SelectItem>
                    <SelectItem value="payment_addition">Payment Addition</SelectItem>
                  </SelectContent>
                </Select>
                {modalType === "recharge_request" && (
                  <p className="text-xs text-amber-600 mt-1">Requires approval before processing. An email will be sent to the configured approvers.</p>
                )}
                {modalType === "payment_addition" && (
                  <p className="text-xs text-green-600 mt-1">Will be directly recorded as Paid.</p>
                )}
              </div>
              <div>
                <Label>Amount (₹) <span className="text-red-500">*</span></Label>
                <Input type="number" value={modalAmount} onChange={(e) => setModalAmount(e.target.value)} placeholder="Enter amount" className="mt-1" min="0" step="0.01" data-testid="amount-input" />
              </div>
              {modalType === "payment_addition" && (
                <>
                  <div>
                    <Label>Payment Date <span className="text-red-500">*</span></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal" data-testid="payment-date-trigger">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(modalPaymentDate, "dd/MM/yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={modalPaymentDate} onSelect={(d) => d && setModalPaymentDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>Transaction Number <span className="text-red-500">*</span></Label>
                    <Input value={modalTxnNumber} onChange={(e) => setModalTxnNumber(e.target.value)} placeholder="Enter transaction / UTR number" className="mt-1" data-testid="txn-number-input" />
                  </div>
                </>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea value={modalNotes} onChange={(e) => setModalNotes(e.target.value)} placeholder="Optional notes" className="mt-1" rows={2} data-testid="notes-input" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePayment} disabled={!modalPartner || !modalType || !modalAmount || submitting} style={{ backgroundColor: "#1B8C3D" }} data-testid="submit-payment-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {submitting ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Mark Paid Modal */}
        <Dialog open={markPaidModalOpen} onOpenChange={setMarkPaidModalOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="mark-paid-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-green-600" /> Mark as Paid</DialogTitle>
              <DialogDescription>Enter the transaction details to confirm payment</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label>Transaction Number <span className="text-red-500">*</span></Label>
              <Input value={markPaidTxn} onChange={(e) => setMarkPaidTxn(e.target.value)} placeholder="Enter transaction / UTR number" className="mt-1" data-testid="mark-paid-txn-input" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter" && markPaidTxn.trim()) handleMarkPaid(); }} />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setMarkPaidModalOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkPaid} disabled={!markPaidTxn.trim()} style={{ backgroundColor: "#1B8C3D" }} data-testid="confirm-mark-paid">
                <CheckCircle className="w-4 h-4 mr-2" /> Confirm Paid
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PnLReport;
